import { useEffect, useRef, useState, type ReactNode } from "react";

/* ---------- Reveal helper ---------- */
function Reveal({ children, delay = 0, className = "" }: { children: ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(18px)",
        transition: `opacity 520ms cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 520ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

/* ---------- Trust bar ---------- */
export function TrustBar() {
  const items = [
    { icon: "ri-star-fill", top: "4.9", sub: "2,400+ Google Reviews" },
    { icon: "ri-shield-check-fill", top: "20+ yrs", sub: "Serving Austin" },
    { icon: "ri-time-fill", top: "24/7", sub: "Emergency Support" },
    { icon: "ri-map-pin-fill", top: "Austin & Surrounding", sub: "Proudly Local" },
  ];
  return (
    <section className="bg-white border-y border-slate-100">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map((i, idx) => (
          <Reveal key={i.top} delay={idx * 80}>
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-lg bg-brand-blue/10 flex items-center justify-center">
                <i className={`${i.icon} text-brand-blue text-lg`}></i>
              </span>
              <div>
                <div className="font-display font-bold text-brand-navy text-[18px]" style={{ fontWeight: 700 }}>
                  {i.top}
                </div>
                <div className="text-[12px] text-brand-navy/60">{i.sub}</div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------- What Do You Need Today ---------- */
export function NeedToday() {
  const cards = [
    {
      k: "Repair",
      title: "It's broken",
      desc: "AC not cooling, furnace not heating, strange noises, high bills. Same-day diagnosis and repair.",
      img: "https://readdy.ai/api/search-image?query=Close%20up%20professional%20HVAC%20technician%20hands%20servicing%20outdoor%20air%20conditioner%20condenser%20with%20tools%2C%20bright%20natural%20daylight%2C%20clean%20suburban%20home%20background%2C%20premium%20service%20photography%2C%20crisp%20high%20detail%2C%20warm%20trustworthy%20human%20centered%20mood&width=1000&height=800&seq=need-repair-01&orientation=landscape",
      tag: "Same-Day",
      bullets: ["Upfront pricing", "All brands", "Warranty included"],
    },
    {
      k: "Replace",
      title: "I need a new system",
      desc: "High-efficiency AC, heat pump, or furnace, sized right for your home. Free in-home estimate.",
      img: "https://readdy.ai/api/search-image?query=Modern%20new%20outdoor%20heat%20pump%20HVAC%20system%20installed%20beside%20a%20clean%20suburban%20home%20on%20green%20lawn%2C%20bright%20sunny%20morning%20light%2C%20crisp%20architectural%20photography%2C%20fresh%20premium%20residential%20mood%2C%20soft%20blue%20sky%20background&width=1000&height=800&seq=need-replace-01&orientation=landscape",
      tag: "0% APR",
      bullets: ["Free estimate", "Flexible financing", "10-year warranty"],
    },
    {
      k: "Maintain",
      title: "Keep it running",
      desc: "Seasonal tune-ups, air quality checks, priority service. Members save 15% on every repair.",
      img: "https://readdy.ai/api/search-image?query=Smart%20round%20thermostat%20on%20a%20clean%20white%20wall%20inside%20a%20bright%20modern%20living%20room%2C%20soft%20natural%20daylight%20from%20window%2C%20minimalist%20interior%20photography%2C%20warm%20premium%20home%20comfort%20atmosphere%2C%20crisp%20detail&width=1000&height=800&seq=need-maintain-01&orientation=landscape",
      tag: "Comfort Club",
      bullets: ["2 visits / year", "Priority scheduling", "No overtime fees"],
    },
  ];
  return (
    <section id="services" className="bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <Reveal>
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="text-[11px] tracking-[0.2em] font-bold text-brand-blue mb-3">WHAT DO YOU NEED TODAY?</div>
              <h2 className="font-display text-[44px] md:text-[56px] leading-[1] font-800 text-brand-navy max-w-[600px]" style={{ fontWeight: 800 }}>
                Three ways we <span className="text-brand-blue">bring the comfort back.</span>
              </h2>
            </div>
            <a href="#book" className="hidden md:inline-flex items-center gap-2 text-brand-navy font-semibold hover:text-brand-blue cursor-pointer">
              See all services <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {cards.map((c, i) => (
            <Reveal key={c.k} delay={i * 90}>
              <a href="#book" className="group block bg-white rounded-2xl border border-slate-100 overflow-hidden hover:border-brand-blue transition-colors cursor-pointer">
                <div className="h-56 overflow-hidden relative">
                  <img src={c.img} alt={c.k} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                  <span className={`absolute top-4 left-4 text-[10px] tracking-[0.16em] font-bold px-2.5 py-1.5 rounded-md ${
                    c.tag === "Same-Day" ? "bg-brand-ember text-white" : "bg-white text-brand-navy"
                  }`}>
                    {c.tag}
                  </span>
                </div>
                <div className="p-7">
                  <div className="text-[11px] tracking-[0.2em] font-bold text-brand-blue mb-2">{c.k.toUpperCase()}</div>
                  <h3 className="font-display text-[26px] font-bold text-brand-navy mb-3" style={{ fontWeight: 700 }}>
                    {c.title}
                  </h3>
                  <p className="text-[14px] text-brand-navy/70 leading-relaxed mb-5">{c.desc}</p>
                  <ul className="space-y-2 mb-6">
                    {c.bullets.map((b) => (
                      <li key={b} className="flex items-center gap-2 text-[13px] text-brand-navy/85">
                        <i className="ri-check-line text-brand-blue"></i> {b}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center gap-2 text-brand-blue font-semibold text-[14px]">
                    Book now <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Count-up number ---------- */
function CountUp({ end, duration = 1400, suffix = "" }: { end: number; duration?: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [val, setVal] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const t0 = performance.now();
        const tick = (t: number) => {
          const p = Math.min(1, (t - t0) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          setVal(Math.round(end * eased));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        io.disconnect();
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
}

/* ---------- Same-Day Service section ---------- */
export function SameDayBand() {
  const rows = [
    { area: "North Austin", eta: "45\u201360 min", hot: true },
    { area: "Downtown", eta: "60\u201390 min", hot: false },
    { area: "Round Rock", eta: "Today", hot: false },
    { area: "Cedar Park", eta: "90\u2013120 min", hot: false },
  ];
  const proofs = [
    { i: "ri-customer-service-2-fill", t: "24/7 Call Support", d: "Local dispatcher, never a call center." },
    { i: "ri-time-fill", t: "Real Arrival Windows", d: "Two-hour promise, tracked live by text." },
    { i: "ri-truck-fill", t: "Stocked Service Vehicles", d: "Most repairs finished on the first visit." },
  ];
  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: "#152A4A", minHeight: "78vh" }}
    >
      {/* subtle depth wash */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 12% 10%, rgba(255,255,255,0.06), transparent 55%), radial-gradient(ellipse at 95% 100%, rgba(200,50,42,0.10), transparent 55%)",
        }}
      ></div>
      {/* faint grid */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      ></div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left copy */}
        <div className="lg:col-span-6">
          <Reveal>
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] font-bold text-brand-ember mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-ember inline-block" style={{ boxShadow: "0 0 0 4px rgba(200,50,42,0.20)" }}></span>
              SAME-DAY SERVICE
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-[48px] md:text-[64px] leading-[1.02] text-white" style={{ fontWeight: 800 }}>
              Same-day means<br />
              <span className="text-white/95">same-day.</span>
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-6 text-white/70 max-w-[500px] leading-relaxed text-[15px]">
              Call before 2 PM and a licensed technician is at your door before dinner. Local dispatchers, stocked trucks, and honest two-hour arrival windows &mdash; no vague "sometime this week."
            </p>
          </Reveal>

          <div className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {proofs.map((p, i) => (
              <Reveal key={p.t} delay={200 + i * 90}>
                <div>
                  <div className="flex items-center gap-2 text-white font-display font-bold text-[14px]" style={{ fontWeight: 700 }}>
                    <span className="w-7 h-7 rounded-md bg-white/10 flex items-center justify-center">
                      <i className={`${p.i} text-white/90 text-[13px]`}></i>
                    </span>
                    {p.t}
                  </div>
                  <div className="mt-2 text-[12.5px] text-white/55 leading-relaxed">{p.d}</div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={520}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#book"
                className="inline-flex items-center gap-2 bg-brand-ember hover:brightness-110 text-white font-semibold px-6 py-3.5 rounded-md whitespace-nowrap cursor-pointer transition-all"
              >
                <i className="ri-calendar-check-line"></i> Book Same-Day Service
              </a>
              <a
                href="tel:5125550187"
                className="inline-flex items-center gap-2 text-white font-semibold hover:text-brand-ember cursor-pointer transition-colors"
              >
                <i className="ri-phone-fill"></i> (512) 555-0187
                <span className="text-white/40 font-normal text-[13px]">&middot; answered 24/7</span>
              </a>
            </div>
          </Reveal>
        </div>

        {/* Right visual */}
        <div className="lg:col-span-6 relative">
          <Reveal delay={120}>
            <div className="relative rounded-2xl overflow-hidden border border-white/10" style={{ aspectRatio: "5/6", maxHeight: "640px" }}>
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20HVAC%20service%20technician%20in%20clean%20uniform%20standing%20confidently%20next%20to%20a%20fully%20stocked%20white%20service%20van%20with%20open%20back%20doors%20showing%20organized%20tools%20and%20parts%2C%20suburban%20Austin%20Texas%20residential%20driveway%20at%20golden%20hour%2C%20warm%20natural%20sunlight%2C%20premium%20editorial%20service%20photography%2C%20cinematic%20mood%2C%20crisp%20high%20detail%2C%20muted%20deep%20blue%20evening%20tones%20in%20background%20with%20subtle%20warm%20highlights&width=1200&height=1440&seq=sameday-hvac-01&orientation=portrait"
                alt="Same-day HVAC service technician with stocked service van"
                className="w-full h-full object-cover object-center"
                style={{ transform: "scale(1)", transition: "transform 1200ms cubic-bezier(0.16,1,0.3,1)" }}
              />
              {/* gradient wash for text legibility */}
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(21,42,74,0.15) 0%, rgba(21,42,74,0.55) 100%)" }}></div>

              {/* Availability card */}
              <div className="absolute left-5 right-5 bottom-5 md:left-6 md:right-auto md:bottom-6 md:w-[340px] rounded-xl bg-white/95 backdrop-blur-sm border border-white/60 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.2em] font-bold text-brand-ember">
                    <span className="relative flex w-2 h-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-brand-ember opacity-60 animate-ping"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-ember"></span>
                    </span>
                    AVAILABLE TODAY
                  </div>
                  <div className="text-[10px] font-medium text-brand-navy/50 tracking-wide">LIVE</div>
                </div>
                <div className="space-y-2.5">
                  {rows.map((r, i) => (
                    <Reveal key={r.area} delay={300 + i * 90}>
                      <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                        <div className="flex items-center gap-2 text-[13px] text-brand-navy font-medium">
                          <i className="ri-map-pin-2-fill text-brand-navy/30 text-[12px]"></i>
                          {r.area}
                        </div>
                        <div className={`text-[12.5px] font-semibold ${r.hot ? "text-brand-ember" : "text-brand-navy/80"}`}>
                          {r.eta}
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-end justify-between">
                  <div>
                    <div className="font-display leading-none text-brand-navy" style={{ fontWeight: 800, fontSize: "32px" }}>
                      <CountUp end={12} />
                    </div>
                    <div className="text-[11px] text-brand-navy/60 mt-1.5">technicians available now</div>
                  </div>
                  <div className="flex -space-x-2">
                    <img src="https://readdy.ai/api/search-image?query=Friendly%20professional%20HVAC%20technician%20portrait%20headshot%20in%20clean%20uniform%2C%20warm%20natural%20studio%20light%2C%20plain%20soft%20neutral%20background%2C%20editorial%20service%20photography&width=80&height=80&seq=tech-avatar-0&orientation=squarish" alt="tech" className="w-7 h-7 rounded-full border-2 border-white object-cover object-top" />
                    <img src="https://readdy.ai/api/search-image?query=Confident%20female%20HVAC%20service%20technician%20portrait%20headshot%20in%20navy%20uniform%2C%20warm%20natural%20studio%20light%2C%20plain%20neutral%20background%2C%20editorial%20service%20photography&width=80&height=80&seq=tech-avatar-1&orientation=squarish" alt="tech" className="w-7 h-7 rounded-full border-2 border-white object-cover object-top" />
                    <img src="https://readdy.ai/api/search-image?query=Experienced%20senior%20HVAC%20technician%20portrait%20headshot%20with%20warm%20smile%20in%20clean%20uniform%2C%20soft%20studio%20lighting%2C%20plain%20neutral%20background%2C%20editorial%20service%20photography&width=80&height=80&seq=tech-avatar-2&orientation=squarish" alt="tech" className="w-7 h-7 rounded-full border-2 border-white object-cover object-top" />
                    <img src="https://readdy.ai/api/search-image?query=Young%20professional%20HVAC%20apprentice%20technician%20portrait%20headshot%20in%20uniform%2C%20warm%20natural%20light%2C%20plain%20soft%20background%2C%20editorial%20service%20photography&width=80&height=80&seq=tech-avatar-3&orientation=squarish" alt="tech" className="w-7 h-7 rounded-full border-2 border-white object-cover object-top" />
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* small floating stat */}
          <Reveal delay={260}>
            <div className="hidden md:block absolute -top-4 -right-4 bg-white rounded-xl border border-slate-100 px-5 py-4">
              <div className="text-[10px] tracking-[0.2em] font-bold text-brand-navy/50">AVG. ARRIVAL</div>
              <div className="font-display text-brand-navy leading-none mt-1" style={{ fontWeight: 800, fontSize: "28px" }}>
                <CountUp end={38} suffix=" min" />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------- Why Choose Us ---------- */
export function WhyChoose() {
  const REFINED_BLUE = "#2C55B8";
  const principles = [
    {
      n: "01",
      i: "ri-file-list-3-line",
      t: "Upfront, before we start",
      d: "You'll understand the issue, your options and the price in plain language — in writing — before any work begins.",
      detail: "Written estimate • no surprise fees",
    },
    {
      n: "02",
      i: "ri-tools-line",
      t: "Repair when repair makes sense",
      d: "We won't push a full replacement when a reliable repair is the better call for your home and your budget.",
      detail: "Honest recommendation, every time",
    },
    {
      n: "03",
      i: "ri-home-heart-line",
      t: "Your home stays your home",
      d: "Shoe covers on, work areas clean, tools organized. We treat every house the way we'd treat our own.",
      detail: "Clean, respectful in-home service",
    },
    {
      n: "04",
      i: "ri-team-line",
      t: "One team from call to completion",
      d: "Local dispatcher, local technician, clear text updates. No handoff maze, no offshore call center in the middle.",
      detail: "Same team, start to finish",
    },
  ];
  const [open, setOpen] = useState<number>(0);
  return (
    <section id="why" className="bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left */}
        <div className="lg:col-span-5 self-center">
          <Reveal>
            <div className="text-[11px] tracking-[0.22em] font-bold mb-4" style={{ color: REFINED_BLUE }}>
              WHY HOMEOWNERS CHOOSE US
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-[44px] md:text-[56px] leading-[1.02] text-brand-navy" style={{ fontWeight: 800 }}>
              What good home<br />
              service actually<br />
              <span style={{ color: REFINED_BLUE }}>feels like.</span>
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-6 text-[15.5px] text-brand-navy/70 leading-relaxed max-w-[440px]">
              After more than twenty years in Austin homes we've learned something simple: the best HVAC visit is the one you barely notice. Clear pricing, careful hands, honest advice — then we're out of your way.
            </p>
          </Reveal>
          <Reveal delay={220}>
            <div className="mt-10 rounded-2xl border border-slate-100 bg-[#FAFBFC] p-6">
              <div className="flex items-center gap-5">
              <div className="flex -space-x-2">
                <img src="https://readdy.ai/api/search-image?query=Warm%20smiling%20female%20homeowner%20portrait%20headshot%2C%20soft%20natural%20window%20light%2C%20plain%20neutral%20light%20background%2C%20editorial%20lifestyle%20photography%2C%20warm%20trustworthy%20tone&width=80&height=80&seq=why-avatar-0&orientation=squarish" alt="customer" className="w-10 h-10 rounded-full border-2 border-white object-cover object-top" />
                <img src="https://readdy.ai/api/search-image?query=Friendly%20middle%20aged%20male%20homeowner%20portrait%20headshot%2C%20soft%20natural%20window%20light%2C%20plain%20light%20neutral%20background%2C%20editorial%20lifestyle%20photography&width=80&height=80&seq=why-avatar-1&orientation=squarish" alt="customer" className="w-10 h-10 rounded-full border-2 border-white object-cover object-top" />
                <img src="https://readdy.ai/api/search-image?query=Kind%20smiling%20older%20female%20homeowner%20portrait%20headshot%20with%20gray%20hair%2C%20soft%20natural%20window%20light%2C%20plain%20light%20background%2C%20editorial%20lifestyle%20photography&width=80&height=80&seq=why-avatar-2&orientation=squarish" alt="customer" className="w-10 h-10 rounded-full border-2 border-white object-cover object-top" />
                <img src="https://readdy.ai/api/search-image?query=Young%20professional%20male%20homeowner%20portrait%20headshot%2C%20soft%20natural%20window%20light%2C%20plain%20neutral%20light%20background%2C%20editorial%20lifestyle%20photography&width=80&height=80&seq=why-avatar-3&orientation=squarish" alt="customer" className="w-10 h-10 rounded-full border-2 border-white object-cover object-top" />
              </div>
              <div>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-brand-ember text-[13px]"></i>
                  ))}
                  <span className="ml-1.5 font-display font-bold text-brand-navy text-[14px]" style={{ fontWeight: 700 }}>4.9</span>
                </div>
                <div className="text-[12px] text-brand-navy/60 mt-0.5">2,400+ Austin homeowners</div>
              </div>
              </div>
              <div className="mt-5 pt-5 border-t border-slate-200/70 grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: "rgba(44,85,184,0.1)" }}>
                    <i className="ri-shield-check-line text-[14px]" style={{ color: REFINED_BLUE }}></i>
                  </span>
                  <div>
                    <div className="font-display font-bold text-brand-navy text-[13px]" style={{ fontWeight: 700 }}>Licensed &amp; insured</div>
                    <div className="text-[11px] text-brand-navy/55">TACLA #48120</div>
                  </div>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: "rgba(44,85,184,0.1)" }}>
                    <i className="ri-home-4-line text-[14px]" style={{ color: REFINED_BLUE }}></i>
                  </span>
                  <div>
                    <div className="font-display font-bold text-brand-navy text-[13px]" style={{ fontWeight: 700 }}>20+ years local</div>
                    <div className="text-[11px] text-brand-navy/55">Family-owned since 2003</div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Right */}
        <div className="lg:col-span-7">
          <div className="rounded-2xl border border-slate-100 bg-[#FAFBFC] overflow-hidden">
            {principles.map((p, i) => {
              const active = open === i;
              return (
                <Reveal key={p.n} delay={i * 80}>
                  <div
                    onMouseEnter={() => setOpen(i)}
                    onClick={() => setOpen(i)}
                    className={`relative px-6 md:px-8 grid grid-cols-[auto_1fr_auto] gap-5 md:gap-6 items-center cursor-pointer transition-all duration-500 ease-out overflow-hidden ${
                      i !== 0 ? "border-t border-slate-100" : ""
                    } ${active ? "bg-white" : ""}`}
                    style={{ minHeight: active ? 200 : 80 }}
                  >
                    {/* left rail accent */}
                    <span
                      className="absolute left-0 top-0 bottom-0 w-[3px] transition-all duration-300"
                      style={{
                        backgroundColor: REFINED_BLUE,
                        opacity: active ? 1 : 0,
                      }}
                    ></span>

                    {/* number */}
                    <div
                      className="font-display leading-none transition-colors duration-300"
                      style={{
                        fontWeight: 700,
                        fontSize: active ? "20px" : "15px",
                        color: active ? REFINED_BLUE : "#94A3B8",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {p.n}
                    </div>

                    {/* body */}
                    <div className="py-4">
                      <h3
                        className="font-display text-brand-navy transition-all duration-300"
                        style={{
                          fontWeight: active ? 800 : 600,
                          fontSize: active ? "22px" : "16px",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {p.t}
                      </h3>
                      <div
                        className="overflow-hidden transition-all duration-500 ease-out"
                        style={{
                          maxHeight: active ? 140 : 0,
                          opacity: active ? 1 : 0,
                          marginTop: active ? 10 : 0,
                        }}
                      >
                        <p className="text-[14px] text-brand-navy/70 leading-relaxed max-w-[500px]">
                          {p.d}
                        </p>
                        <div className="mt-3 inline-flex items-center gap-2 text-[12px] font-semibold" style={{ color: REFINED_BLUE }}>
                          <i className="ri-check-line"></i> {p.detail}
                        </div>
                      </div>
                    </div>

                    {/* icon */}
                    <div className="flex items-center">
                      <span
                        className="rounded-lg flex items-center justify-center transition-all duration-300"
                        style={{
                          backgroundColor: active ? REFINED_BLUE : "#EEF2F7",
                          color: active ? "#ffffff" : "#64748B",
                          width: active ? 44 : 36,
                          height: active ? 44 : 36,
                        }}
                      >
                        <i className={`${p.i}`} style={{ fontSize: active ? 18 : 15 }}></i>
                      </span>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={480}>
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-[12.5px] text-brand-navy/60">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line" style={{ color: REFINED_BLUE }}></i>
                Licensed &amp; insured • TACLA #48120
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-award-line" style={{ color: REFINED_BLUE }}></i>
                NATE-certified technicians
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-home-4-line" style={{ color: REFINED_BLUE }}></i>
                Family-owned since 2003
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------- Comfort System ---------- */
export function ComfortSystem() {
  const REFINED_BLUE_C = "#2C55B8";
  const cards = [
    {
      k: "thermostat",
      n: "01",
      label: "SMART CONTROL",
      t: "Smart control",
      d: "Control temperature, schedules and comfort settings with a modern thermostat that learns how your home actually lives.",
      img: "https://readdy.ai/api/search-image?query=Modern%20round%20smart%20thermostat%20mounted%20on%20a%20clean%20matte%20white%20wall%20inside%20a%20bright%20residential%20living%20room%2C%20soft%20natural%20daylight%20from%20side%20window%2C%20minimal%20warm%20interior%2C%20premium%20editorial%20product%20photography%2C%20crisp%20high%20detail%2C%20subtle%20shadows%2C%20neutral%20cool%20tones%2C%20no%20text%20no%20logo&width=800&height=1000&seq=comfort-card-thermostat-1&orientation=portrait",
    },
    {
      k: "indoor",
      n: "02",
      label: "INDOOR UNIT",
      t: "Indoor comfort",
      d: "The indoor unit manages airflow and delivers balanced, quiet comfort throughout every room of the home.",
      img: "https://readdy.ai/api/search-image?query=Modern%20white%20indoor%20HVAC%20air%20handler%20furnace%20unit%20standing%20in%20a%20clean%20bright%20residential%20utility%20closet%2C%20premium%20equipment%2C%20editorial%20product%20photography%2C%20soft%20natural%20light%2C%20crisp%20high%20detail%2C%20minimal%20neutral%20background%2C%20subtle%20shadows%2C%20no%20text%20no%20logo&width=800&height=1000&seq=comfort-card-indoor-1&orientation=portrait",
    },
    {
      k: "outdoor",
      n: "03",
      label: "OUTDOOR UNIT",
      t: "Outdoor performance",
      d: "The outdoor unit powers efficient heating and cooling in the toughest Texas weather, day after day.",
      img: "https://readdy.ai/api/search-image?query=Modern%20outdoor%20HVAC%20condenser%20heat%20pump%20unit%20installed%20on%20clean%20concrete%20pad%20beside%20a%20bright%20residential%20home%20exterior%2C%20soft%20natural%20morning%20daylight%2C%20premium%20architectural%20product%20photography%2C%20crisp%20high%20detail%2C%20minimal%20neutral%20background%2C%20subtle%20shadows%2C%20no%20text%20no%20logo&width=800&height=1000&seq=comfort-card-outdoor-1&orientation=portrait",
    },
  ];
  const [active, setActive] = useState(0);
  const hoverTimer = useRef<number | null>(null);
  const pendingIdx = useRef<number | null>(null);
  const requestActivate = (i: number) => {
    if (i === active) return;
    if (pendingIdx.current === i) return;
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    pendingIdx.current = i;
    hoverTimer.current = window.setTimeout(() => {
      setActive(i);
      pendingIdx.current = null;
      hoverTimer.current = null;
    }, 200);
  };
  const cancelActivate = () => {
    if (hoverTimer.current) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    pendingIdx.current = null;
  };
  useEffect(() => () => { if (hoverTimer.current) window.clearTimeout(hoverTimer.current); }, []);
  const sectionRef2 = useRef<HTMLDivElement>(null);
  const [inView2, setInView2] = useState(false);
  useEffect(() => {
    const el = sectionRef2.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView2(true); io.disconnect(); }
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const activeCard = cards[active];

  return (
    <section id="systems" className="bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center lg:min-h-[72vh]">
        {/* LEFT ~40% */}
        <div className="lg:col-span-5">
          <Reveal>
            <div className="text-[11px] tracking-[0.22em] font-bold mb-3" style={{ color: REFINED_BLUE_C }}>
              SYSTEM UPGRADE
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-[38px] md:text-[46px] leading-[1.05] text-brand-navy" style={{ fontWeight: 800 }}>
              Better comfort starts with the <span style={{ color: REFINED_BLUE_C }}>right system.</span>
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-4 text-[14.5px] text-brand-navy/70 leading-relaxed max-w-[420px]">
              A comfortable home isn't one machine &mdash; it's three parts working as one, sized and tuned around how your home actually lives.
            </p>
          </Reveal>

          {/* Synchronized active content */}
          <Reveal delay={200}>
            <div className="mt-8 relative min-h-[168px]">
              {cards.map((c, i) => (
                <div
                  key={c.k}
                  className="absolute inset-0 transition-all duration-500"
                  style={{
                    opacity: i === active ? 1 : 0,
                    transform: i === active ? "translateY(0)" : "translateY(8px)",
                    pointerEvents: i === active ? "auto" : "none",
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="font-display text-[15px] pt-1"
                      style={{ fontWeight: 700, letterSpacing: "0.08em", color: REFINED_BLUE_C }}
                    >
                      {c.n}
                    </div>
                    <div>
                      <div className="font-display text-brand-navy text-[22px]" style={{ fontWeight: 800, letterSpacing: "-0.01em" }}>
                        {c.t}
                      </div>
                      <div className="text-[14px] text-brand-navy/70 leading-relaxed mt-2 max-w-[400px]">
                        {c.d}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Progress dots (also acts as mobile pager) */}
          <Reveal delay={280}>
            <div className="mt-6 flex items-center gap-2">
              {cards.map((c, i) => (
                <button
                  key={c.k}
                  onClick={() => setActive(i)}
                  aria-label={`Show ${c.t}`}
                  className="h-1.5 rounded-full transition-all cursor-pointer"
                  style={{
                    width: i === active ? 28 : 10,
                    backgroundColor: i === active ? REFINED_BLUE_C : "#CBD5E1",
                  }}
                ></button>
              ))}
            </div>
          </Reveal>

          <Reveal delay={360}>
            <div className="mt-6 flex flex-wrap items-center gap-5">
              <a
                href="#book"
                className="inline-flex items-center gap-2 text-white font-semibold px-5 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                style={{ backgroundColor: REFINED_BLUE_C }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#22459A")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = REFINED_BLUE_C)}
              >
                Explore System Upgrades <i className="ri-arrow-right-line"></i>
              </a>
              <a href="#financing" className="inline-flex items-center gap-2 text-brand-navy/75 hover:text-brand-navy text-[14px] font-semibold cursor-pointer">
                <i className="ri-bank-card-line" style={{ color: REFINED_BLUE_C }}></i> Flexible financing available
              </a>
            </div>
          </Reveal>
        </div>

        {/* RIGHT ~60% */}
        <div className="lg:col-span-7">
          {/* Desktop: stacked cards */}
          <div
            ref={sectionRef2}
            className="hidden lg:block relative"
            style={{
              opacity: inView2 ? 1 : 0,
              transform: inView2 ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 800ms cubic-bezier(0.16,1,0.3,1), transform 800ms cubic-bezier(0.16,1,0.3,1)",
              height: 520,
            }}
          >
            <div className="relative w-full h-full flex items-center justify-end pr-2">
              <div className="relative h-[88%]" style={{ width: "calc(60% + 200px)" }}>
                {cards.map((c, i) => {
                  const isActive = i === active;
                  const baseX = i * 100;
                  const z = 100 - Math.abs(i - active) * 10 + i;
                  return (
                    <div
                      key={c.k}
                      onMouseEnter={() => requestActivate(i)}
                      onMouseLeave={cancelActivate}
                      className="absolute top-0 bottom-0 rounded-2xl overflow-hidden bg-white border border-slate-100 cursor-pointer"
                      style={{
                        left: 0,
                        width: "60%",
                        transform: `translateX(${baseX}px)`,
                        zIndex: z,
                        boxShadow: isActive
                          ? "0 36px 70px -22px rgba(21,42,74,0.32), 0 14px 26px -12px rgba(21,42,74,0.18)"
                          : "0 10px 24px -12px rgba(21,42,74,0.12)",
                        transition: "box-shadow 600ms cubic-bezier(0.16,1,0.3,1), border-color 400ms ease-out, filter 500ms ease-out",
                        filter: isActive ? "none" : `brightness(${1 - Math.abs(i - active) * 0.04})`,
                      }}
                    >
                      <img
                        src={c.img}
                        alt={c.t}
                        className="w-full h-full object-cover object-center transition-transform duration-[600ms] ease-out"
                        style={{ transform: isActive ? "scale(1.03)" : "scale(1)" }}
                      />
                      <div
                        className="absolute inset-x-0 bottom-0 p-5"
                        style={{ background: "linear-gradient(180deg, transparent, rgba(21,42,74,0.75))" }}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-[10px] tracking-[0.2em] font-bold text-white/80">{c.label}</div>
                            <div className="font-display text-white text-[20px] mt-1" style={{ fontWeight: 700 }}>
                              {c.t}
                            </div>
                          </div>
                          <div
                            className="font-display text-white/85 text-[13px]"
                            style={{ fontWeight: 700, letterSpacing: "0.08em" }}
                          >
                            {c.n}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>

          {/* Mobile: single card pager */}
          <div className="lg:hidden">
            <div className="relative rounded-2xl overflow-hidden bg-white border border-slate-100" style={{ aspectRatio: "4/5" }}>
              {cards.map((c, i) => (
                <div
                  key={c.k}
                  className="absolute inset-0 transition-opacity duration-500"
                  style={{ opacity: i === active ? 1 : 0, pointerEvents: i === active ? "auto" : "none" }}
                >
                  <img src={c.img} alt={c.t} className="w-full h-full object-cover object-center" />
                  <div className="absolute inset-x-0 bottom-0 p-5" style={{ background: "linear-gradient(180deg, transparent, rgba(21,42,74,0.75))" }}>
                    <div className="text-[10px] tracking-[0.2em] font-bold text-white/80">{c.label}</div>
                    <div className="font-display text-white text-[20px] mt-1" style={{ fontWeight: 700 }}>{c.t}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-center gap-4">
              <button
                onClick={() => setActive((a) => (a - 1 + cards.length) % cards.length)}
                className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-brand-navy cursor-pointer"
                aria-label="Previous"
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <div className="text-[12px] text-brand-navy/60">
                <span className="font-semibold text-brand-navy">{activeCard.n}</span> / 03 &middot; {activeCard.t}
              </div>
              <button
                onClick={() => setActive((a) => (a + 1) % cards.length)}
                className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-brand-navy cursor-pointer"
                aria-label="Next"
              >
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- How It Works ---------- */
export function HowItWorks() {
  const ACCENT = "#C8322A";
  const steps = [
    { n: "01", t: "Book online or call", d: "Pick a same-day slot in under 60 seconds. Confirmation by text." },
    { n: "02", t: "Meet your tech", d: "Track your technician in real-time. Photo, name, ETA — no surprises." },
    { n: "03", t: "Upfront quote", d: "We diagnose, explain, and quote in writing. You approve first." },
    { n: "04", t: "Comfort restored", d: "Most repairs done same visit. Follow-up call to make sure it's perfect." },
  ];
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeCount, setActiveCount] = useState(0);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        io.disconnect();
        steps.forEach((_, i) => {
          window.setTimeout(() => setActiveCount((c) => Math.max(c, i + 1)), 220 + i * 120);
        });
      }
    }, { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const progressPct = activeCount === 0 ? 0 : ((activeCount - 1) / (steps.length - 1)) * 100;

  return (
    <section id="how" className="bg-slate-50">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <Reveal>
          <div className="max-w-[700px] mb-14">
            <div className="text-[11px] tracking-[0.2em] font-bold text-brand-blue mb-3">HOW IT WORKS</div>
            <h2 className="font-display text-[44px] md:text-[56px] leading-[1] font-800 text-brand-navy" style={{ fontWeight: 800 }}>
              Four steps from <span className="text-brand-blue">hot to happy.</span>
            </h2>
          </div>
        </Reveal>

        <div ref={sectionRef} className="relative">
          {/* Progress line (desktop only, aligned to card top area) */}
          <div className="hidden lg:block absolute left-0 right-0 pointer-events-none" style={{ top: 46 }}>
            <div className="relative mx-8 h-[2px] bg-slate-200 rounded-full overflow-hidden">
              <div
                className="absolute left-0 top-0 h-full rounded-full"
                style={{
                  width: `${progressPct}%`,
                  backgroundColor: ACCENT,
                  transition: "width 700ms cubic-bezier(0.16,1,0.3,1)",
                }}
              ></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            {steps.map((s, i) => {
              const isActive = i < activeCount;
              const isHover = hoverIdx === i && isActive;
              return (
                <div
                  key={s.n}
                  onMouseEnter={() => setHoverIdx(i)}
                  onMouseLeave={() => setHoverIdx((h) => (h === i ? null : h))}
                  className="bg-white rounded-xl p-7 h-full relative"
                  style={{
                    border: `1px solid ${isActive ? (isHover ? "rgba(200,50,42,0.55)" : "rgba(200,50,42,0.28)") : "#F1F5F9"}`,
                    boxShadow: isActive
                      ? (isHover
                          ? "0 18px 40px -20px rgba(200,50,42,0.32), 0 6px 14px -8px rgba(21,42,74,0.12)"
                          : "0 10px 28px -16px rgba(21,42,74,0.18)")
                      : "0 1px 2px rgba(21,42,74,0.03)",
                    transform: `translateY(${isActive ? (isHover ? -8 : -6) : 0}px)`,
                    transition:
                      "transform 600ms cubic-bezier(0.16,1,0.3,1), box-shadow 500ms ease-out, border-color 400ms ease-out",
                  }}
                >
                  {/* connector node on the line */}
                  <div className="hidden lg:block absolute -top-[7px] left-7">
                    <span
                      className="block w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: isActive ? ACCENT : "#CBD5E1",
                        boxShadow: isActive ? "0 0 0 4px rgba(200,50,42,0.18)" : "none",
                        transition: "background-color 400ms ease, box-shadow 400ms ease",
                      }}
                    ></span>
                  </div>
                  <div
                    className="font-display font-800 text-[52px] leading-none"
                    style={{
                      fontWeight: 800,
                      color: isActive ? ACCENT : "rgba(44,85,184,0.15)",
                      transition: "color 500ms ease-out",
                    }}
                  >
                    {s.n}
                  </div>
                  <h3 className="font-display font-bold text-brand-navy text-[20px] mt-2" style={{ fontWeight: 700 }}>{s.t}</h3>
                  <p className="text-[14px] text-brand-navy/70 mt-2 leading-relaxed">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Reviews ---------- */
export function Reviews() {
  const reviews = [
    { n: "Sarah M.", loc: "Barton Hills", t: "Called at 9 AM, cool by lunch. Tech was polite, wore booties inside, explained everything. Fair price, no upsell.", r: 5 },
    { n: "David & Rachel K.", loc: "Circle C", t: "Replaced our 14-yr-old system. Bills dropped 34% first month. Financing was painless.", r: 5 },
    { n: "Marcus T.", loc: "Mueller", t: "The Comfort Club is worth every penny. Two tune-ups a year and they always show up on time.", r: 5 },
    { n: "Priya S.", loc: "Round Rock", t: "Best HVAC experience we've had in 12 years of owning this house. Highly recommend Summit.", r: 5 },
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 5000);
    return () => clearInterval(t);
  }, []);
  return (
    <section id="reviews" className="bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
        <Reveal className="lg:col-span-2">
          <div>
            <div className="text-[11px] tracking-[0.2em] font-bold text-brand-blue mb-3">REAL REVIEWS</div>
            <h2 className="font-display text-[44px] md:text-[56px] leading-[1] font-800 text-brand-navy" style={{ fontWeight: 800 }}>
              Neighbors talking about <span className="text-brand-blue">Summit.</span>
            </h2>
            <div className="mt-6 flex items-center gap-4">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <i key={s} className="ri-star-fill text-brand-ember text-xl"></i>
                ))}
              </div>
              <div>
                <div className="font-display font-bold text-brand-navy text-[20px]" style={{ fontWeight: 700 }}>4.9 / 5</div>
                <div className="text-[12px] text-brand-navy/60">2,400+ verified Google reviews</div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={140} className="lg:col-span-3">
          <div className="relative bg-slate-50 rounded-2xl p-10 border border-slate-100 min-h-[280px]">
            <i className="ri-double-quotes-l text-brand-blue/20 text-6xl absolute top-4 left-6"></i>
            {reviews.map((r, i) => (
              <div
                key={r.n}
                className="absolute inset-0 p-10 flex flex-col justify-center transition-opacity duration-700"
                style={{ opacity: i === idx ? 1 : 0, pointerEvents: i === idx ? "auto" : "none" }}
              >
                <p className="font-display text-[22px] md:text-[26px] leading-snug text-brand-navy" style={{ fontWeight: 600 }}>
                  "{r.t}"
                </p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-blue text-white flex items-center justify-center font-bold">
                    {r.n.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-brand-navy text-[14px]">{r.n}</div>
                    <div className="text-[12px] text-brand-navy/60">{r.loc}, TX</div>
                  </div>
                </div>
              </div>
            ))}
            <div className="absolute bottom-6 right-6 flex gap-2">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`h-2 rounded-full transition-all cursor-pointer ${
                    i === idx ? "w-8 bg-brand-blue" : "w-2 bg-brand-navy/20"
                  }`}
                ></button>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Financing + Plan ---------- */
export function FinancingPlan() {
  return (
    <section id="financing" className="bg-slate-50">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Reveal>
          <div className="bg-white rounded-2xl p-10 border border-slate-100 h-full">
            <div className="text-[11px] tracking-[0.2em] font-bold text-brand-blue mb-3">FINANCING</div>
            <h3 className="font-display text-[36px] leading-[1.05] font-800 text-brand-navy" style={{ fontWeight: 800 }}>
              0% APR for 24 months on approved credit.
            </h3>
            <p className="mt-5 text-brand-navy/70 leading-relaxed">
              New systems shouldn't break the bank. We offer flexible plans starting under $89/month with approvals in under 60 seconds.
            </p>
            <ul className="mt-6 space-y-2">
              {["Soft credit check", "Approvals in ~60 seconds", "No prepayment penalty"].map((b) => (
                <li key={b} className="flex items-center gap-2 text-[14px] text-brand-navy/85">
                  <i className="ri-check-line text-brand-blue"></i> {b}
                </li>
              ))}
            </ul>
            <a href="#book" className="mt-8 inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blueDark text-white font-semibold px-6 py-3.5 rounded-md whitespace-nowrap cursor-pointer">
              Check Rates <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="bg-brand-navy rounded-2xl p-10 h-full text-white relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-brand-blue/30 blur-3xl"></div>
            <div className="relative">
              <div className="text-[11px] tracking-[0.2em] font-bold text-brand-ember mb-3">COMFORT CLUB</div>
              <h3 className="font-display text-[36px] leading-[1.05] font-800" style={{ fontWeight: 800 }}>
                $19/mo maintenance plan.
              </h3>
              <p className="mt-5 text-white/70 leading-relaxed">
                Two tune-ups per year, priority scheduling, 15% off repairs, no overtime fees. Cancel any time.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {[
                  { k: "2×", v: "Tune-ups yearly" },
                  { k: "15%", v: "Off any repair" },
                  { k: "$0", v: "Overtime fees" },
                  { k: "Priority", v: "Booking access" },
                ].map((s) => (
                  <div key={s.k} className="bg-white/5 border border-white/10 rounded-lg p-3">
                    <div className="font-display font-bold text-[20px]" style={{ fontWeight: 700 }}>{s.k}</div>
                    <div className="text-white/60 text-[12px] mt-0.5">{s.v}</div>
                  </div>
                ))}
              </div>
              <a href="#book" className="mt-8 inline-flex items-center gap-2 bg-white text-brand-navy hover:bg-slate-100 font-semibold px-6 py-3.5 rounded-md whitespace-nowrap cursor-pointer">
                Join Comfort Club <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Service Area ---------- */
export function ServiceArea() {
  const areas = ["Austin", "Round Rock", "Cedar Park", "Pflugerville", "Leander", "Georgetown", "Lakeway", "Bee Cave", "Buda", "Kyle", "Manor", "Hutto"];
  return (
    <section id="area" className="bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div>
            <div className="text-[11px] tracking-[0.2em] font-bold text-brand-blue mb-3">SERVICE AREA</div>
            <h2 className="font-display text-[44px] md:text-[56px] leading-[1] font-800 text-brand-navy" style={{ fontWeight: 800 }}>
              Proudly serving <span className="text-brand-blue">Greater Austin.</span>
            </h2>
            <p className="mt-5 text-brand-navy/70 max-w-[500px] leading-relaxed">
              Family-owned, local dispatchers, local trucks. If you can see the Frost Tower, we're on our way.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {areas.map((a) => (
                <span key={a} className="text-[13px] font-medium px-3.5 py-1.5 rounded-full bg-slate-50 border border-slate-100 text-brand-navy whitespace-nowrap">
                  <i className="ri-map-pin-line text-brand-blue mr-1"></i> {a}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="rounded-2xl overflow-hidden border border-slate-100 h-[440px]">
            <iframe
              title="Service area map"
              className="w-full h-full"
              src="https://www.google.com/maps?q=Austin,TX&output=embed"
              loading="lazy"
            ></iframe>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Booking CTA + Form ---------- */
export function BookingCta() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errMsg, setErrMsg] = useState("");

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const hp = String(fd.get("website_alt") || "").trim();
    if (hp) {
      setStatus("success");
      form.reset();
      return;
    }
    fd.delete("website_alt");

    setStatus("loading");
    setErrMsg("");
    try {
      const params = new URLSearchParams();
      fd.forEach((v, k) => params.append(k, String(v)));
      const res = await fetch("https://readdy.ai/api/form/dagjh3jep053ijub2bf0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      const msg = parsed?.meta?.message || parsed?.meta?.detail || text;
      if (res.ok && parsed?.code === "OK" && !/spam/i.test(msg)) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setErrMsg(msg || "Submission failed. Please try again.");
      }
    } catch {
      setStatus("error");
      setErrMsg("Network error. Please try again.");
    }
  };

  return (
    <section id="book" className="relative bg-brand-navy overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-30" style={{
        backgroundImage: "radial-gradient(circle at 15% 20%, rgba(44,85,184,0.55), transparent 60%), radial-gradient(circle at 85% 80%, rgba(200,50,42,0.32), transparent 60%)",
      }}></div>
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid grid-cols-1 lg:grid-cols-5 gap-12">
        <Reveal className="lg:col-span-2">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] font-bold text-brand-ember bg-white/10 border border-brand-ember/30 px-3 py-1.5 rounded-full mb-6">
              <i className="ri-time-fill"></i> AVAILABLE TODAY
            </div>
            <h2 className="font-display text-[44px] md:text-[56px] leading-[1] font-800 text-white" style={{ fontWeight: 800 }}>
              Let's get your <span className="text-brand-blue">comfort back.</span>
            </h2>
            <p className="mt-5 text-white/70 max-w-[420px] leading-relaxed">
              Tell us what's going on. A local dispatcher will confirm within 15 minutes and lock in a same-day slot.
            </p>
            <div className="mt-8 space-y-3 text-white/80 text-[14px]">
              <div className="flex items-center gap-3"><i className="ri-phone-fill text-brand-blue"></i> (512) 555-0187 · 24/7</div>
              <div className="flex items-center gap-3"><i className="ri-mail-fill text-brand-blue"></i> hello@summithomeservices.com</div>
              <div className="flex items-center gap-3"><i className="ri-map-pin-fill text-brand-blue"></i> 1200 W 6th St, Austin, TX 78703</div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={140} className="lg:col-span-3">
          <form
            onSubmit={submit}
            data-readdy-form
            id="hvac-service-request"
            className="bg-white rounded-2xl p-8 md:p-10 border border-white/10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[12px] font-semibold text-brand-navy/80">First name</span>
                <input required name="first_name" type="text" className="mt-1.5 w-full px-4 py-3 rounded-md border border-slate-200 focus:border-brand-blue focus:outline-none text-sm" />
              </label>
              <label className="block">
                <span className="text-[12px] font-semibold text-brand-navy/80">Last name</span>
                <input required name="last_name" type="text" className="mt-1.5 w-full px-4 py-3 rounded-md border border-slate-200 focus:border-brand-blue focus:outline-none text-sm" />
              </label>
              <label className="block">
                <span className="text-[12px] font-semibold text-brand-navy/80">Email</span>
                <input required name="email" type="email" className="mt-1.5 w-full px-4 py-3 rounded-md border border-slate-200 focus:border-brand-blue focus:outline-none text-sm" />
              </label>
              <label className="block">
                <span className="text-[12px] font-semibold text-brand-navy/80">Phone</span>
                <input required name="phone" type="tel" className="mt-1.5 w-full px-4 py-3 rounded-md border border-slate-200 focus:border-brand-blue focus:outline-none text-sm" />
              </label>
              <label className="block md:col-span-2">
                <span className="text-[12px] font-semibold text-brand-navy/80">Service address</span>
                <input required name="address" type="text" className="mt-1.5 w-full px-4 py-3 rounded-md border border-slate-200 focus:border-brand-blue focus:outline-none text-sm" placeholder="Street, City" />
              </label>
              <label className="block">
                <span className="text-[12px] font-semibold text-brand-navy/80">Service needed</span>
                <select required name="service_type" className="mt-1.5 w-full px-4 py-3 rounded-md border border-slate-200 focus:border-brand-blue focus:outline-none text-sm bg-white">
                  <option value="">Choose one…</option>
                  <option>Same-Day Repair</option>
                  <option>New System Estimate</option>
                  <option>Maintenance / Tune-Up</option>
                  <option>Air Quality</option>
                  <option>Other</option>
                </select>
              </label>
              <label className="block">
                <span className="text-[12px] font-semibold text-brand-navy/80">Preferred time</span>
                <select name="preferred_time" className="mt-1.5 w-full px-4 py-3 rounded-md border border-slate-200 focus:border-brand-blue focus:outline-none text-sm bg-white">
                  <option>Today (ASAP)</option>
                  <option>Tomorrow morning</option>
                  <option>Tomorrow afternoon</option>
                  <option>This week</option>
                </select>
              </label>
              <label className="block md:col-span-2">
                <span className="text-[12px] font-semibold text-brand-navy/80">What's going on? (optional)</span>
                <textarea name="details" maxLength={500} rows={3} className="mt-1.5 w-full px-4 py-3 rounded-md border border-slate-200 focus:border-brand-blue focus:outline-none text-sm resize-none" placeholder="Not cooling, strange noise, high bills…" />
              </label>
            </div>

            {/* honeypot */}
            <div className="hp-field" aria-hidden="true">
              <label>Website
                <input tabIndex={-1} autoComplete="off" type="text" name="website_alt" defaultValue="" />
              </label>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-[12px] text-brand-navy/60 flex items-center gap-2">
                <i className="ri-lock-line text-brand-blue"></i> Your info stays private. No spam, ever.
              </div>
              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center gap-2 bg-brand-ember hover:brightness-110 disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-md whitespace-nowrap cursor-pointer transition-all w-full sm:w-auto justify-center"
              >
                {status === "loading" ? (<><i className="ri-loader-4-line animate-spin"></i> Sending…</>) : (<><i className="ri-calendar-check-line"></i> Book Service</>)}
              </button>
            </div>

            {status === "success" && (
              <div className="mt-4 p-4 rounded-md bg-brand-blue/10 border border-brand-blue/20 text-brand-navy text-sm flex items-start gap-2">
                <i className="ri-check-line text-brand-blue mt-0.5"></i>
                <div>Thanks! Our dispatcher will call you within 15 minutes to confirm your slot.</div>
              </div>
            )}
            {status === "error" && (
              <div className="mt-4 p-4 rounded-md bg-brand-ember/10 border border-brand-ember/20 text-brand-navy text-sm flex items-start gap-2">
                <i className="ri-error-warning-line text-brand-ember mt-0.5"></i>
                <div>{errMsg}</div>
              </div>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Footer ---------- */
export function Footer() {
  return (
    <footer className="bg-brand-navy text-white/80">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-brand-blue flex items-center justify-center">
              <i className="ri-snowflake-line text-white text-lg"></i>
            </div>
            <div className="font-display text-white text-[16px] font-bold" style={{ fontWeight: 700 }}>
              Summit Home Services
            </div>
          </div>
          <p className="mt-4 text-[13px] text-white/60 max-w-[280px] leading-relaxed">
            Family-owned HVAC serving Austin since 2003. Heating, cooling, and air quality done right.
          </p>
          <div className="mt-5 flex gap-3">
            {["ri-facebook-fill", "ri-instagram-line", "ri-google-fill", "ri-youtube-fill"].map((i) => (
              <a key={i} href="#" className="w-9 h-9 rounded-md bg-white/10 hover:bg-brand-blue flex items-center justify-center cursor-pointer transition-colors">
                <i className={`${i} text-white`}></i>
              </a>
            ))}
          </div>
        </div>
        {[
          { t: "Services", links: ["AC Repair", "AC Installation", "Heating", "Heat Pumps", "Air Quality", "Maintenance"] },
          { t: "Company", links: ["About Us", "Reviews", "Financing", "Comfort Club", "Careers", "Blog"] },
          { t: "Contact", links: ["(512) 555-0187", "hello@summithomeservices.com", "1200 W 6th St, Austin TX", "Open 24/7 · Every day"] },
        ].map((c) => (
          <div key={c.t}>
            <div className="text-white font-display font-bold text-[14px] mb-4 tracking-wide" style={{ fontWeight: 700 }}>{c.t}</div>
            <ul className="space-y-2.5">
              {c.links.map((l) => (
                <li key={l}><a href="#" className="text-[13px] text-white/70 hover:text-brand-blue cursor-pointer">{l}</a></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-[12px] text-white/50">
          <div>© {new Date().getFullYear()} Summit Home Services. TACLA #48120. Licensed & Insured.</div>
          <div className="flex gap-5">
            <a href="#" className="hover:text-white cursor-pointer">Privacy</a>
            <a href="#" className="hover:text-white cursor-pointer">Terms</a>
            <a href="#" className="hover:text-white cursor-pointer">Accessibility</a>
          </div>
        </div>
      </div>
    </footer>
  );
}